import type { SkillDefinition, SkillId, SkillSelection } from "../../schemas"

/**
 * Exponential stat cost curve: cost(x, a) = (a^x - 1) / (a^2 - 1)
 *
 * Key properties:
 * - cost(0) = 0 for any a
 * - cost(2) = 1 exactly (the benchmark: 2 stats = 1 cost)
 * - each additional point costs more than the last (convex curve)
 *
 * Constants: a=1.2 for attack, a=1.3 for health (health slightly pricier).
 * Returns a raw float; caller should Math.ceil the final total.
 */
export function computeStatCost(attack: number, health: number): number {
  return expStatCost(attack, ATTACK_A) + expStatCost(health, HEALTH_A)
}

const ATTACK_A = 1.2
const HEALTH_A = 1.3

function expStatCost(x: number, a: number): number {
  if (x <= 0) return 0
  return (a ** x - 1) / (a ** 2 - 1)
}

export const ALLY_REQUIREMENT_DISCOUNT: Record<1 | 2, number> = {
  1: 0.5,
  2: 1,
}

const MODIFIER_SKILL_MIN_COST = 0.5

function dynamicBaseCost(skillId: SkillId, cardCostBeforeSkill: number) {
  switch (skillId) {
    case "recycle":
      return cardCostBeforeSkill >= 5 ? 0.5 : 2
    default:
      return undefined
  }
}

export type SkillLookup = ReadonlyMap<SkillId, SkillDefinition>

export type SkillCostBreakdown = {
  skillCostDelta: number
  nonExhaustSurcharge: number
  selfDestructSurcharge: number
}

export function computeSkillCostBreakdown(
  skills: readonly SkillSelection[],
  skillLookup: SkillLookup,
  baseCardCost = 0,
): SkillCostBreakdown {
  let runningCardCost = baseCardCost
  return skills.reduce<SkillCostBreakdown>(
    (sum, sel) => {
      const def = skillLookup.get(sel.skillId)
      if (!def) return sum
      const baseCost =
        dynamicBaseCost(sel.skillId, runningCardCost) ?? def.baseCostDelta
      const allyDiscount =
        sel.allyRequirement && def.supportsAllyRequirement
          ? ALLY_REQUIREMENT_DISCOUNT[sel.allyRequirement]
          : 0
      const nonExhaustDelta =
        sel.mode === "non_exhausting" ? def.nonExhaustSurcharge : 0
      const selfDestructDelta =
        sel.mode === "self_destruct" ? def.selfDestructSurcharge : 0
      const rawSkillCost =
        baseCost + nonExhaustDelta + selfDestructDelta - allyDiscount
      const shouldClamp =
        def.supportsAllyRequirement ||
        nonExhaustDelta !== 0 ||
        selfDestructDelta !== 0
      const effectiveSkillCost = shouldClamp
        ? Math.max(MODIFIER_SKILL_MIN_COST, rawSkillCost)
        : rawSkillCost
      const clampAdjustment = effectiveSkillCost - rawSkillCost

      sum.skillCostDelta += baseCost - allyDiscount + clampAdjustment

      if (nonExhaustDelta !== 0) {
        sum.nonExhaustSurcharge += nonExhaustDelta
      }

      if (selfDestructDelta !== 0) {
        sum.selfDestructSurcharge += selfDestructDelta
      }

      runningCardCost += effectiveSkillCost

      return sum
    },
    {
      skillCostDelta: 0,
      nonExhaustSurcharge: 0,
      selfDestructSurcharge: 0,
    },
  )
}
